/* ============================================================
 * Custom reviews page.
 *
 * Reads Judge.me's own widget endpoint, which needs no API token.
 * Two independent streams:
 *   wall  show_media_only=true,  filtered to minRating and above
 *   list  show_media_only=false, media reviews removed so nothing repeats
 *
 * Paging is 24 per request, the endpoint's cap regardless of per_page.
 * Pages were verified non-overlapping across the full 630-review set,
 * so fetching the next page blind never repeats a review.
 *
 * Two things are deliberately eager rather than lazy:
 *   1. Neighbouring viewer images are preloaded, and a swap waits for
 *      decode, so the photo and the words change on the same frame.
 *   2. The next page of reviews is buffered in the background, so
 *      Show more renders from memory instead of pausing on a fetch.
 * ============================================================ */
(function () {
  'use strict';

  var API = 'https://cdn.judge.me/widgets/reviews_grid_widget_data';
  var SWAP_CAP = 700;   // never hold the text longer than this waiting on an image

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function decode(s) {
    var t = document.createElement('textarea');
    t.innerHTML = String(s == null ? '' : s);
    return t.value;
  }
  function widen(url, px) {
    if (!url) return '';
    return url.indexOf('width=') > -1 ? url.replace(/width=\d+/, 'width=' + px) : url;
  }
  function vthumb(r, px) {
    // Cloudflare Stream poster for a video-only review (video uploads clear
    // pictures_urls, so the tile would otherwise have no image at all).
    var v = r && r.video && r.video.thumbnail_urls && r.video.thumbnail_urls[0];
    return v ? v.replace(/height=\d+/, 'height=' + px) : '';
  }
  function picOf(r, px) {
    var p = r && r.pictures_urls && r.pictures_urls[0];
    var u = p && (p.original || p.huge);
    return u ? widen(u, px) : vthumb(r, px);
  }
  function hasMedia(r) {
    return (r.pictures_urls && r.pictures_urls.length) ||
           r.video || (r.video_external_ids && r.video_external_ids.length) ||
           (r.media_platform_hosted_video_infos && r.media_platform_hosted_video_infos.length);
  }
  function isVideo(r) {
    return !!(r.video || (r.video_external_ids && r.video_external_ids.length) ||
              (r.media_platform_hosted_video_infos && r.media_platform_hosted_video_infos.length));
  }
  function stars(n) {
    var out = '';
    for (var i = 0; i < 5; i++) {
      out += '<svg class="cc-rv__st cc-rv__st--' + (i < n ? 'on' : 'off') +
             '" viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.6l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.2l-4.94 2.6.94-5.5-4-3.9 5.53-.8z"/></svg>';
    }
    return '<span class="cc-rv__stars" role="img" aria-label="' + n + ' out of 5 stars">' + out + '</span>';
  }
  function dateText(iso) {
    var d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  var imgCache = {};
  function preload(src) {
    if (!src) return null;
    if (imgCache[src]) return imgCache[src];
    var im = new Image();
    im.decoding = 'async';
    im.src = src;
    imgCache[src] = im;
    return im;
  }

  function init(root) {
    var shop      = root.dataset.shop;
    var minRating = parseInt(root.dataset.minRating, 10) || 4;
    var wallFirst = parseInt(root.dataset.wallFirst, 10) || 12;
    var wallStep  = parseInt(root.dataset.wallStep, 10) || 12;
    var listFirst = parseInt(root.dataset.listFirst, 10) || 12;
    var listStep  = parseInt(root.dataset.listStep, 10) || 24;

    var wallEl = root.querySelector('[data-rv-wall]');
    var listEl = root.querySelector('[data-rv-list]');
    var moreP  = root.querySelector('[data-rv-more-photos]');
    var moreT  = root.querySelector('[data-rv-more-text]');
    var viewer = root.querySelector('[data-rv-viewer]');
    var lb     = root.querySelector('[data-rv-lb]');
    var lbView = lb ? lb.querySelector('[data-rv-lbviewer]') : null;

    var media = [], mediaSeen = {}, mediaPage = 0, mediaDone = false, mediaBusy = null;
    var text  = [], textSeen  = {}, textPage  = 0, textDone  = false, textBusy  = null;
    var wallShown = 0, listShown = 0, vIdx = 0, lbIdx = 0, lastFocus = null;

    function url(mediaOnly, page) {
      return API + '?shop_domain=' + encodeURIComponent(shop) +
        '&platform=shopify&review_selection=all&per_page=24&page=' + page +
        '&show_media_only=' + (mediaOnly ? 'true' : 'false') +
        (mediaOnly ? '&display_order=media_first' : '');
    }

    // one in-flight request per stream, callers share the same promise
    // One fetch path for both streams: a 10 minute sessionStorage cache
    // (a reload costs zero API calls), and 3 tries with backoff before
    // giving up. cdn.judge.me rate limits per IP, and its 429s used to be
    // read as "no more reviews", hiding the button and blanking the page.
    function fetchPage(mediaOnly, page) {
      var key = 'ccrv1:' + shop + ':' + (mediaOnly ? 'm' : 't') + ':' + page;
      try {
        var hit = JSON.parse(sessionStorage.getItem(key));
        if (hit && hit.t > Date.now() - 600000) return Promise.resolve(hit.d);
      } catch (e) {}
      var attempt = function (n) {
        return fetch(url(mediaOnly, page))
          .then(function (r) {
            if (!r.ok) throw new Error('http ' + r.status);
            return r.json();
          })
          .then(function (j) {
            try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), d: j })); } catch (e) {}
            return j;
          })
          .catch(function (err) {
            if (n >= 3) throw err;
            return new Promise(function (res) { setTimeout(res, 1200 * n); })
              .then(function () { return attempt(n + 1); });
          });
      };
      return attempt(1);
    }

    // Both loaders resolve true when the call worked (even if it proved the
    // stream finished) and false when the network failed. Failure never sets
    // the done flag: a later click or auto-retry simply tries again.
    function loadMedia() {
      if (mediaDone) return Promise.resolve(true);
      if (mediaBusy) return mediaBusy;
      mediaBusy = fetchPage(true, mediaPage + 1)
        .then(function (j) {
          mediaBusy = null;
          if (!j || !j.reviews || !j.reviews.length) { mediaDone = true; return true; }
          mediaPage = j.current_page || mediaPage + 1;
          if (j.total_pages && mediaPage >= j.total_pages) mediaDone = true;
          j.reviews.forEach(function (r) {
            if (mediaSeen[r.uuid]) return;
            if ((r.rating || 0) < minRating) return;
            if (!hasMedia(r)) return;
            mediaSeen[r.uuid] = 1;
            media.push(r);
          });
          return true;
        })
        .catch(function () { mediaBusy = null; return false; });
      return mediaBusy;
    }

    function loadText() {
      if (textDone) return Promise.resolve(true);
      if (textBusy) return textBusy;
      textBusy = fetchPage(false, textPage + 1)
        .then(function (j) {
          textBusy = null;
          if (!j || !j.reviews || !j.reviews.length) { textDone = true; return true; }
          textPage = j.current_page || textPage + 1;
          if (j.total_pages && textPage >= j.total_pages) textDone = true;
          j.reviews.forEach(function (r) {
            if (textSeen[r.uuid]) return;
            if (hasMedia(r)) return;      // media lives in the wall, never repeated here
            textSeen[r.uuid] = 1;
            text.push(r);
          });
          return true;
        })
        .catch(function () { textBusy = null; return false; });
      return textBusy;
    }

    // keep a page in hand so Show more never waits on the network
    function bufferMedia() {
      if (mediaDone || mediaBusy) return;
      if (media.length - wallShown < wallStep * 2) {
        loadMedia().then(function () { warmTiles(); });
      }
    }
    function bufferText() {
      if (textDone || textBusy) return;
      if (text.length - listShown < listStep * 2) loadText();
    }
    // fetch the image files for the batch that Show more will reveal next
    function warmTiles() {
      media.slice(wallShown, wallShown + wallStep).forEach(function (r) { preload(picOf(r, 520)); });
    }
    function warmViewer(i) {
      if (!media.length) return;
      // One neighbour each side. Two each side preloaded four offscreen
      // 1200px slides on page load, ~350 KB nobody had asked to see.
      for (var k = -1; k <= 1; k++) {
        var r = media[((i + k) % media.length + media.length) % media.length];
        if (r) preload(picOf(r, 1200));
      }
    }

    function tile(r, i) {
      var prod = decode(r.product_title || '');
      var play = isVideo(r)
        ? '<span class="cc-rv__play"><svg viewBox="0 0 20 20"><path d="M5 3l12 7-12 7z"/></svg></span>' : '';
      return '<div class="cc-rv__cell"><button class="cc-rv__ph" type="button" data-rv-open="' + i +
        '" aria-label="Open review from ' + esc(r.reviewer_name || 'a customer') + '">' +
        '<img src="' + esc(picOf(r, 520)) + '" alt="Customer photo for ' + esc(prod) + '" loading="lazy">' + play +
        '<span class="cc-rv__cap"><b>' + esc(r.reviewer_name || 'Anonymous') + '</b> &middot; ' + esc(prod) +
        '</span></button></div>';
    }

    // The wall used CSS multi-column masonry, which balances by total
    // height: the column bottoms drifted apart, and every image that
    // finished decoding rebalanced the whole wall, so new batches arrived
    // in visible waves with tiles hopping between columns. The wall is now
    // explicit column divs. Each new tile goes to the currently shortest
    // column, and existing tiles never move unless the responsive column
    // count itself changes.
    var colQuery = window.matchMedia('(max-width: 767px)');
    var cols = [], colH = [];

    function estHeight(cell) { return parseFloat(cell.dataset.ar || '1.2') + 0.14; }

    function shortestCol() {
      var k = 0;
      for (var i = 1; i < colH.length; i++) if (colH[i] < colH[k]) k = i;
      return k;
    }

    function placeCell(cell) {
      var k = shortestCol();
      cols[k].appendChild(cell);
      colH[k] += estHeight(cell);
    }

    function ensureCols() {
      var want = colQuery.matches ? 2 : 3;
      if (cols.length === want && cols[0] && cols[0].parentNode === wallEl) return;
      var cells = [].slice.call(wallEl.querySelectorAll('.cc-rv__cell'));
      cells.sort(function (a, b) {
        return (parseInt(a.querySelector('[data-rv-open]').dataset.rvOpen, 10) || 0) -
               (parseInt(b.querySelector('[data-rv-open]').dataset.rvOpen, 10) || 0);
      });
      wallEl.innerHTML = '';
      cols = []; colH = [];
      for (var i = 0; i < want; i++) {
        var c = document.createElement('div');
        c.className = 'cc-rv__col';
        wallEl.appendChild(c);
        cols.push(c); colH.push(0);
      }
      cells.forEach(placeCell);
    }
    if (colQuery.addEventListener) colQuery.addEventListener('change', ensureCols);
    else colQuery.addListener(ensureCols);

    function buildCell(r, i, img) {
      var d = document.createElement('div');
      d.innerHTML = tile(r, i);
      var cell = d.firstChild;
      var ar = img && img.naturalWidth ? img.naturalHeight / img.naturalWidth : 1.2;
      cell.dataset.ar = ar.toFixed(3);
      return cell;
    }

    var wallBusy = false;
    function renderWall(count) {
      // Decode the whole batch off-DOM first, then place it in one pass.
      // Appending raw <img> tags had each image pop in on its own network
      // and decode schedule. A slow image cannot hold the batch: it is
      // placed anyway after 1.5s and finishes loading where it stands.
      count = Math.min(count, media.length);
      if (count <= wallShown || wallBusy) return Promise.resolve();
      wallBusy = true;
      if (moreP) moreP.disabled = true;
      ensureCols();
      var start = wallShown;
      var items = media.slice(start, count);
      wallShown = count;
      var imgs = items.map(function (r) { return preload(picOf(r, 520)); });
      var settled = imgs.map(function (im) {
        if (!im || (im.complete && im.naturalWidth)) return Promise.resolve();
        return new Promise(function (res) {
          var t = setTimeout(res, 1500);
          var done = function () { clearTimeout(t); res(); };
          im.decode ? im.decode().then(done, done) : (im.onload = done, im.onerror = done);
        });
      });
      return Promise.all(settled).then(function () {
        items.forEach(function (r, k) { placeCell(buildCell(r, start + k, imgs[k])); });
        wallBusy = false;
        if (moreP) {
          moreP.disabled = false;
          moreP.hidden = (mediaDone && wallShown >= media.length);
        }
        warmTiles();
      });
    }

    function row(r) {
      var prod = decode(r.product_title || '');
      var link = r.product_url ? '<a href="' + esc(r.product_url) + '">' + esc(prod) + '</a>' : esc(prod);
      return '<div class="cc-rv__row"><div>' + stars(r.rating || 0) +
        '<div class="cc-rv__rdate">' + esc(dateText(r.created_at)) + '</div></div>' +
        '<div><div class="cc-rv__rbody">' + esc(r.body || '') + '</div>' +
        '<div class="cc-rv__rwho">' + esc(r.reviewer_name || 'Anonymous') +
        (r.verified_buyer ? '<span class="cc-rv__vb">Verified buyer</span>' : '') + '</div></div>' +
        '<div class="cc-rv__rprod">' + link + '</div></div>';
    }

    function renderList(count) {
      var next = text.slice(0, count);
      listEl.innerHTML = next.map(row).join('');
      listShown = next.length;
      if (moreT) moreT.hidden = (textDone && listShown >= text.length);
    }

    /* Paint image and words on the same frame. If the photo is already
       decoded, which prefetching makes the normal case, this is instant.
       If not, hold the text briefly rather than let it jump ahead. */
    function paint(el, r) {
      if (!el || !r) return;
      var src = picOf(r, 1200);
      var prod = decode(r.product_title || '');
      var apply = function () {
        var im = el.querySelector('[data-rv-vimg]');
        im.src = src;
        im.alt = 'Customer photo for ' + prod;
        el.querySelector('[data-rv-vstars]').innerHTML = stars(r.rating || 0);
        el.querySelector('[data-rv-vprod]').innerHTML =
          r.product_url ? '<a href="' + esc(r.product_url) + '">' + esc(prod) + '</a>' : esc(prod);
        var q = el.querySelector('[data-rv-vquote]');
        q.innerHTML = '<p>' + esc(r.body || '') + '</p>';
        q.scrollTop = 0;
        el.querySelector('[data-rv-vmeta]').innerHTML =
          '<strong>' + esc(r.reviewer_name || 'Anonymous') + '</strong>' +
          (r.verified_buyer ? '<span class="cc-rv__vb">Verified buyer</span>' : '') +
          '<span>' + esc(dateText(r.created_at)) + '</span>';
        // Video reviews: overlay a Stream player so the viewer plays the
        // video instead of showing a still. The img stays (hidden) to size
        // the box; non-video slides put it back and park the iframe.
        var box = im.parentElement;
        var fr = box.querySelector('iframe');
        var uid = isVideo(r) && r.video_external_ids && r.video_external_ids[0];
        if (uid) {
          if (!fr) {
            fr = document.createElement('iframe');
            fr.setAttribute('allow', 'accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture');
            fr.setAttribute('allowfullscreen', '');
            fr.title = 'Customer video review';
            fr.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0';
            box.style.position = 'relative';
            box.appendChild(fr);
          }
          var vs = 'https://iframe.videodelivery.net/' + uid;
          if (fr.src !== vs) fr.src = vs;
          fr.style.display = '';
          im.style.visibility = 'hidden';
        } else {
          if (fr) { fr.style.display = 'none'; fr.src = 'about:blank'; }
          im.style.visibility = '';
        }
      };
      // Rapid arrow taps start overlapping paints, and an older paint's
      // 700ms cap could fire after a newer one already applied, flashing a
      // stale review. Only the newest paint for this element may apply.
      var gen = el.__ccPaintGen = (el.__ccPaintGen || 0) + 1;
      var pre = preload(src);
      if (!pre || (pre.complete && pre.naturalWidth)) { apply(); return; }
      var fired = false;
      var once = function () {
        if (!fired && el.__ccPaintGen === gen) { fired = true; apply(); }
      };
      var cap = setTimeout(once, SWAP_CAP);
      var settle = function () { clearTimeout(cap); once(); };
      if (pre.decode) pre.decode().then(settle, settle);
      else { pre.onload = settle; pre.onerror = settle; }
    }

    function ensureAhead(i) {
      if (!mediaDone && i > media.length - 8) loadMedia();
      warmViewer(i);
    }
    function setViewer(i) {
      if (!media.length) return;
      vIdx = ((i % media.length) + media.length) % media.length;
      paint(viewer, media[vIdx]);
      ensureAhead(vIdx);
    }
    function setLb(i) {
      if (!media.length) return;
      lbIdx = ((i % media.length) + media.length) % media.length;
      paint(lbView, media[lbIdx]);
      ensureAhead(lbIdx);
    }
    function openLb(i) {
      lastFocus = document.activeElement;
      setLb(i);
      lb.classList.add('is-open');
      document.body.classList.add('cc-rv-locked');
      lb.querySelector('[data-rv-close]').focus();
    }
    function closeLb() {
      lb.classList.remove('is-open');
      document.body.classList.remove('cc-rv-locked');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    root.addEventListener('click', function (e) {
      var open = e.target.closest('[data-rv-open]');
      if (open) { openLb(parseInt(open.dataset.rvOpen, 10) || 0); return; }

      var nav = e.target.closest('[data-rv-nav]');
      if (nav) {
        var d = parseInt(nav.dataset.rvDir, 10);
        if (nav.dataset.rvNav === 'lb') setLb(lbIdx + d); else setViewer(vIdx + d);
        return;
      }
      if (e.target.closest('[data-rv-close]') || e.target === lb) { closeLb(); return; }

      if (e.target.closest('[data-rv-more-photos]')) {
        var wantW = wallShown + wallStep;
        if (media.length > wallShown) { renderWall(Math.min(wantW, media.length)); bufferMedia(); }
        else { moreP.disabled = true; loadMedia().then(function () {
          moreP.disabled = false; renderWall(Math.min(wantW, media.length)); bufferMedia(); }); }
        return;
      }
      if (e.target.closest('[data-rv-more-text]')) {
        var wantL = listShown + listStep;
        if (text.length > listShown) { renderList(Math.min(wantL, text.length)); bufferText(); }
        else { moreT.disabled = true; loadText().then(function () {
          moreT.disabled = false; renderList(Math.min(wantL, text.length)); bufferText(); }); }
      }
    });

    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLb();
      if (e.key === 'ArrowLeft') setLb(lbIdx - 1);
      if (e.key === 'ArrowRight') setLb(lbIdx + 1);
    });

    // If the very first loads fail (rate limit window), retry quietly on a
    // widening delay instead of declaring the page empty. The page-2
    // prefetches that used to fire here are gone: the first click fetches
    // (and caches) its page on demand, halving the calls each load spends
    // against the shared per-IP limit.
    var wallRetryDelays = [5000, 12000, 30000];
    var wallRetryN = 0;
    var listRetryDelays = [7000, 15000, 32000];
    var listRetryN = 0;

    function fillMedia() {
      return loadMedia().then(function (ok) {
        if (ok && media.length < wallFirst && !mediaDone) return fillMedia();
        return ok;
      });
    }
    function startWall() {
      fillMedia().then(function (ok) {
        if (!media.length) {
          if (!ok && wallRetryN < wallRetryDelays.length) {
            setTimeout(startWall, wallRetryDelays[wallRetryN++]);
            return;
          }
          if (viewer) viewer.hidden = true;
          wallEl.innerHTML = '<p class="cc-rv__empty">No photo reviews to show yet.</p>';
          if (moreP) moreP.hidden = true;
          return;
        }
        renderWall(Math.min(wallFirst, media.length));
        setViewer(0);
        root.classList.add('cc-rv--ready');
      });
    }
    startWall();

    function fillText() {
      return loadText().then(function (ok) {
        if (ok && text.length < listFirst && !textDone) return fillText();
        return ok;
      });
    }
    function startList() {
      fillText().then(function (ok) {
        if (!text.length) {
          if (!ok && listRetryN < listRetryDelays.length) {
            setTimeout(startList, listRetryDelays[listRetryN++]);
            return;
          }
          listEl.innerHTML = '<p class="cc-rv__empty">No written reviews to show yet.</p>';
          if (moreT) moreT.hidden = true;
          return;
        }
        renderList(Math.min(listFirst, text.length));
      });
    }
    startList();
  }

  function boot() { document.querySelectorAll('[data-rv-root]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
